import {SelectedFile} from "core/transfer/selection";

/**
 * Folder-aware drop capture (ui#94): turns a drop's `DataTransfer` into the selection model,
 * traversing dropped directories through the callback-based FileSystem Entries API
 * (`webkitGetAsEntry`). Before this, a dropped FOLDER arrived via `dataTransfer.files` as an
 * unreadable pseudo-File and produced a broken transfer.
 *
 * Contract:
 * - No Entries API (old browsers, jsdom) or no directory among the items → the plain `File`s,
 *   returned SYNCHRONOUSLY — plain file drops behave exactly as before.
 * - At least one directory → a Promise of the traversed {@link DroppedCapture}; each nested file's
 *   `relativePath` starts with the dropped folder's own name (matching `webkitdirectory` paths).
 * - A failing directory read or file materialization SKIPS that entry — it never kills the drop.
 * - Folders that yielded no file are REPORTED (ui#1308), never silently swallowed: a transfer is a
 *   list of files, so an empty folder cannot travel, and the caller owes the sender that fact.
 */

/**
 * MUST be called synchronously inside the drop event: `DataTransferItem`s (and therefore
 * `webkitGetAsEntry`/`getAsFile`) go stale once the event handler yields. The returned entries
 * stay usable asynchronously, which is where the (batched, callback-based) traversal happens.
 */
export function captureDroppedFiles(dataTransfer: DataTransfer): File[] | Promise<DroppedCapture> {
	const items = dataTransfer.items;
	if (!items || items.length === 0 || typeof items[0].webkitGetAsEntry !== "function") {
		return Array.from(dataTransfer.files ?? []);
	}
	// Synchronous snapshot: entry AND file per item, while both accessors still work.
	const roots = Array.from(items)
		.filter(item => item.kind === "file")
		.map(item => ({entry: item.webkitGetAsEntry(), file: item.getAsFile()}));
	if (!roots.some(root => root.entry?.isDirectory === true)) {
		// Only plain files (or entry-less items): exactly the pre-folder behavior, synchronously.
		return roots.map(root => root.file).filter((file): file is File => file !== null);
	}
	return traverseRoots(roots);
}

/** A traversed drop: the files it produced, plus the folders that produced none. */
export interface DroppedCapture {
	files: SelectedFile[];
	/**
	 * Paths of folders that yielded no file, in traversal order. Only the OUTERMOST such folder of a
	 * nested chain is listed: reporting `a`, `a/b` and `a/b/c` for one barren tree would name three
	 * losses where the sender dropped one.
	 *
	 * "Yielded no file", NOT "was empty" — the Entries API cannot tell the two apart. A folder whose
	 * directory read fails, or whose every file fails to materialize, produces nothing and lands here
	 * too, which is why the copy this drives never asserts that the folder was empty (ui#1308).
	 */
	foldersWithoutFiles: string[];
}

async function traverseRoots(roots: Array<{entry: FileSystemEntry | null; file: File | null}>): Promise<DroppedCapture> {
	const capture: DroppedCapture = {files: [], foldersWithoutFiles: []};
	for (const root of roots) {
		if (root.entry?.isDirectory === true) {
			// The dropped folder's own name heads every path, like webkitdirectory picker paths.
			const name = root.entry.name;
			if (!await traverseDirectory(root.entry as FileSystemDirectoryEntry, name, capture)) {
				capture.foldersWithoutFiles.push(name);
			}
		} else if (root.file !== null) {
			// A root-level file drop stays flat — no folder, no path.
			capture.files.push({file: root.file});
		}
	}
	return capture;
}

/**
 * Depth-first traversal; `prefix` is the folder path so far (no trailing slash). Returns whether this
 * folder contributed any file, so the caller can decide whether to report it — a barren SUBFOLDER
 * inside an otherwise-populated tree is just as lost as a barren dropped root (ui#1308).
 */
async function traverseDirectory(directory: FileSystemDirectoryEntry, prefix: string, out: DroppedCapture): Promise<boolean> {
	// Where THIS subtree's reports begin. A barren branch rewinds to here, which collapses the inner
	// skeleton it already reported into the one folder the sender actually dropped.
	//
	// A watermark and not a `startsWith(prefix)` filter: two roots dropped in one gesture can share a
	// name (`/a/photos` and `/b/photos` both head their paths `photos/`), and a path filter would then
	// match the OTHER root's reports — erasing them and naming a populated folder as barren. An index
	// cannot confuse two subtrees, and it drops the repeated O(n) scan the filter cost.
	const mark = out.foldersWithoutFiles.length;
	let produced = false;
	for (const entry of await readAllEntries(directory)) {
		if (entry.isDirectory) {
			const childPath = `${prefix}/${entry.name}`;
			if (await traverseDirectory(entry as FileSystemDirectoryEntry, childPath, out)) {
				produced = true;
			} else {
				out.foldersWithoutFiles.push(childPath);
			}
		} else if (entry.isFile) {
			const file = await fileOf(entry as FileSystemFileEntry);
			// A failed materialization skips THIS entry only — the rest of the drop survives.
			if (file !== null) {
				out.files.push({file, relativePath: `${prefix}/${entry.name}`});
				produced = true;
			}
		}
	}
	if (!produced) {
		out.foldersWithoutFiles.length = mark;
	}
	return produced;
}

/**
 * Drains one directory reader. `readEntries` returns results in BATCHES (Chromium caps them at
 * ~100) and MUST be re-called until it yields an empty array — a single call silently truncates
 * large folders. A read error resolves with whatever was collected so far (skip, don't kill).
 */
function readAllEntries(directory: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
	const reader = directory.createReader();
	return new Promise(resolve => {
		const collected: FileSystemEntry[] = [];
		const readBatch = () => {
			reader.readEntries(batch => {
				if (batch.length === 0) {
					resolve(collected);
					return;
				}
				collected.push(...batch);
				readBatch();
			}, () => resolve(collected));
		};
		readBatch();
	});
}

/** Promisified `FileSystemFileEntry.file()`; a failure yields `null` (the entry is skipped). */
function fileOf(entry: FileSystemFileEntry): Promise<File | null> {
	return new Promise(resolve => entry.file(resolve, () => resolve(null)));
}
